import { FlowWithRunPermission, GetFlowsWithRunPermissionsRequest } from '~src/ts/ServerApi/requests/GetFlowsWithRunPermissionsRequest';
import { SubscriptionFlow } from '~src/ts/ServerApi/types/SubscriptionFlow';

import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { removeDraftIdPrefix } from '../../drafts/utils/DraftUtils';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { FlowMapper } from '../../ts/mappers/FlowMapper';
import { FlowBaseInfo, FlowBasicInfo, FlowDetailInfo, FlowListItemInfo } from '../../ts/models/FlowInfo';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { ITaggableContent } from '../../ts/models/ITaggableContent';
import { FlowDraftInfo } from '../../ts/models/resourceInfoParts';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { AddTagsToFlowsRequest } from '../../ts/ServerApi/requests/AddTagsToFlowsRequest';
import { CreateRunFlowTaskRequest } from '../../ts/ServerApi/requests/CreateRunFlowTaskRequest';
import { DeleteFlowDraftsRequest } from '../../ts/ServerApi/requests/DeleteFlowDraftsRequest';
import { DeleteFlowsRequest } from '../../ts/ServerApi/requests/DeleteFlowsRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetFlowActionsRequest } from '../../ts/ServerApi/requests/GetFlowActionsRequest';
import { GetFlowDraftActionsRequest } from '../../ts/ServerApi/requests/GetFlowDraftActionsRequest';
import { GetFlowDraftRequest } from '../../ts/ServerApi/requests/GetFlowDraftRequest';
import { GetFlowInputStepsRequest } from '../../ts/ServerApi/requests/GetFlowInputStepsRequest';
import { GetFlowOutputStepsRequest } from '../../ts/ServerApi/requests/GetFlowOutputStepsRequest';
import { GetFlowsRequest } from '../../ts/ServerApi/requests/GetFlowsRequest';
import { MoveFlowDraftsToProjectRequest } from '../../ts/ServerApi/requests/MoveFlowDraftsToProjectRequest';
import { MoveFlowsToProjectRequest } from '../../ts/ServerApi/requests/MoveFlowsToProjectRequest';
import { PerformExtractEncryptionActionOnFlowDraftsRequest } from '../../ts/ServerApi/requests/PerformExtractEncryptionActionOnFlowDraftsRequest';
import { PerformExtractEncryptionActionOnFlowsRequest } from '../../ts/ServerApi/requests/PerformExtractEncryptionActionOnFlowsRequest';
import { RemoveTagsFromFlowsRequest } from '../../ts/ServerApi/requests/RemoveTagsFromFlowsRequest';
import { RevertFlowRequest, RevertFlowResult } from '../../ts/ServerApi/requests/RevertFlowRequest';
import { RunFlowOnOutputStepsRequest } from '../../ts/ServerApi/requests/RunFlowsOnOutputStepsRequest';
import { RunFlowsRequest } from '../../ts/ServerApi/requests/RunFlowsRequest';
import { SetFlowDescriptionRequest } from '../../ts/ServerApi/requests/SetFlowDescriptionRequest';
import { SetFlowDraftNameRequest } from '../../ts/ServerApi/requests/SetFlowDraftNameRequest';
import { SetFlowNameRequest } from '../../ts/ServerApi/requests/SetFlowNameRequest';
import { SetFlowsOwnerRequest } from '../../ts/ServerApi/requests/SetFlowsOwnerRequest';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { FlowDraftAllowedActions } from '../../ts/ServerApi/types/FlowDraftAllowedActions';
import { FlowInputStep } from '../../ts/ServerApi/types/FlowInputStep';
import { FlowOutputStep } from '../../ts/ServerApi/types/FlowOutputStep';
import { UnencryptedConnectionKeychain } from '../../ts/ServerApi/types/IConnectionKeyChain';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { IFlowRunSpecOutputStepViewModel } from '../../ts/ServerApi/types/IFlowRunSpecOutputStepViewModel';

export class FlowsResource implements IListResource<FlowListItemInfo>, IFieldValuesResource, IDetailResource<FlowDetailInfo>, IActionResource<FlowAllowedActions>, ITaggableContent {

  private listFetcher: IListFetcher<FlowListItemInfo>;
  private listWithPermissionsFetcher: IListFetcher<FlowWithRunPermission>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<FlowDetailInfo>;

  constructor(
  ) {
    this.listFetcher = new WebClientListResourceFetcher(GetFlowsRequest, ServerApiMapper.flows);
    this.listWithPermissionsFetcher = new WebClientListResourceFetcher(GetFlowsWithRunPermissionsRequest, ServerApiMapper.flowsWithPermissions);
    this.detailFetcher = new WebClientDetailFetcher('getFlow', FlowMapper.mapDetail);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<FlowListItemInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public allFlowsWithPermission(): ResourceQuery<FlowWithRunPermission> {
    return new ResourceQuery(this.listWithPermissionsFetcher);
  }

  public actions(id: string): Promise<FlowAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowActionsRequest(id));
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public draftActions(draftId: string): Promise<FlowDraftAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowDraftActionsRequest(removeDraftIdPrefix(draftId)));
  }

  public renameDraft(draftId: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetFlowDraftNameRequest({ id: removeDraftIdPrefix(draftId), name: name }));
  }

  public moveDraftsToProject(targetProjectId: string, draftIds: string[]): Promise<IResult> {
    const draftIdsWithoutPrefix = draftIds.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) => new MoveFlowDraftsToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd));
  }

  public deleteFlowDrafts(draftIds: string[]): Promise<IResult> {
    const draftIdsWithoutPrefix = draftIds.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) => new DeleteFlowDraftsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public moveToProject(targetProjectId: string, flowIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(flowIds, (idsBlock) => new MoveFlowsToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public getById(id: string): Promise<FlowDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getDraft(draftId: string): Promise<FlowDraftInfo> {
    return WebHttpClient.sendRequest(new GetFlowDraftRequest({ id: removeDraftIdPrefix(draftId) }));
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'flow');
  }

  public getFlowsByDatasourceId(datasourceId: string): Promise<FlowListItemInfo[]> {
    const query = this.all().addFilter(ServerFilterClauses.serverFilterClauses.datasource(datasourceId));
    return SliceableHelper.fetchAll(query).then(listResult => listResult.items);
  }

  public getInputSteps(flowId: string): Promise<ReadonlyArray<FlowInputStep>> {
    return WebHttpClient.sendRequest(new GetFlowInputStepsRequest({ flowId }))
      .then((result) => result.inputSteps || []); // what to do in the case of an error?
  }

  public getOutputSteps(flowId: string): Promise<ReadonlyArray<FlowOutputStep>> {
    return WebHttpClient.sendRequest(new GetFlowOutputStepsRequest({ flowId }))
      .then((result) => result.outputSteps || []); // what to do in the case of an error?
  }

  public getOutputStepInfos(flowId: string): Promise<ReadonlyArray<FlowOutputStepInfo>> {
    return WebHttpClient.sendRequest(new GetFlowOutputStepsRequest({ flowId }))
      .then((result) => ServerApiMapper.flowOutputSteps(result));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetFlowNameRequest({ id: id, name: name }));
  }

  public runFlow(ids: string[], isRunAllIncremental: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunFlowsRequest(idsBlock, isRunAllIncremental)),
      ResultHelper.combineResults
    );
  }

  public runFlowOnOutputSteps(flowId: string, flowOutputStepIds: string[], isIncremental: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new RunFlowOnOutputStepsRequest({
        flowId: flowId,
        flowOutputStepIds: flowOutputStepIds,
        flowRunSpecOutputStepParams: flowOutputStepIds.map(id => ({ isIncremental: isIncremental, flowOutputStepId: parseInt(id, 10) }))
      }));
  }

  public setDescription(id: string, description: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new SetFlowDescriptionRequest(id, description));
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetFlowsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public createRunFlowTask(
    id: string,
    scheduleId: string,
    runAllOutputs: boolean,
    isRunAllIncremental: boolean,
    flowOutputStepIds: string[],
    flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[],
    shouldEmailWhenDone: boolean,
    subscriptionFlow?: SubscriptionFlow): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateRunFlowTaskRequest({
        scheduleId: scheduleId,
        flowId: id,
        runAllOutputs: runAllOutputs,
        isRunAllIncremental: isRunAllIncremental,
        flowOutputStepIds: flowOutputStepIds,
        flowRunSpecOutputStepParams: flowRunSpecOutputStepParams,
        shouldEmailWhenDone: shouldEmailWhenDone,
        subscriptionFlow: subscriptionFlow !== null && shouldEmailWhenDone ? subscriptionFlow : null
      }));
  }

  public revertFlow(id: string, version: number, keychains?: UnencryptedConnectionKeychain[], encryptedPasswordKeyId?: string): Promise<RevertFlowResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RevertFlowRequest({
      id,
      version,
      keychains,
      encryptedPasswordKeyId
    }));
  }

  public deleteFlows(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteFlowsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public encryptFlows(ids: string[], actionType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnFlowsRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  // Encrypt unparented flow draft
  public encryptFlowDrafts(ids: string[], actionType: string): Promise<IResult> {
    const draftIdsWithoutPrefix = ids.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) =>
        new PerformExtractEncryptionActionOnFlowDraftsRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults
    );
  }

  public hasDataQualityWarning(contentItem: FlowBaseInfo): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators[0]
      && contentItem.dataQualityIndicators[0].isActive;
  }

  public isFlowInfo(item: any): item is FlowBasicInfo {
    return item &&
      item.contentType &&
      item.contentType === 'flow' &&
      item.hasOwnProperty('project');
  }
}

export const Flows = new FlowsResource();
